import { BUTTON_LABELS, FEATURE_FLAGS } from '@/constants';
import { ButtonProps } from '@/components/Buttons';
import { IPersona, ISimulationScore, ISimulation } from '@/types';
import { mapSimulationToRoleplayCard } from '@/utils';
import { MouseEvent, useMemo } from 'react';
import { RoleplayCard, RoleplayCardProps } from '@/components';

interface Props {
  certificationPassed: boolean;
  certificationSimulation: ISimulation;
  getRedFailBorder: (simulation: ISimulation, scores: ISimulationScore[]) => string;
  isLoading: boolean;
  onShareWithManager: RoleplayCardProps['onShareWithManager'];
  onStartClick: (simulation: ISimulation) => (event?: MouseEvent<HTMLButtonElement>) => void;
  onViewCertificateClick: () => void;
  onViewScoreCard: RoleplayCardProps['onViewScoreCard'];
  personas: IPersona[];
  scores: ISimulationScore[];
  userId?: string;
}

/**
 * CertificationSimulationsCertification is responsible for rendering a single roleplay
 * card that represents the final simulation for a certification. It uses the
 * `mapSimulationToRoleplayCard` utility to transform the `certificationSimulation`
 * object into the appropriate props for the `RoleplayCard` component, and it
 * also computes the action button configuration based on the simulation's status
 * and history.
 */
export const CertificationSimulationsCertification: React.FC<Props> = ({
  certificationPassed,
  certificationSimulation,
  getRedFailBorder,
  isLoading,
  onShareWithManager,
  onStartClick,
  onViewCertificateClick,
  onViewScoreCard,
  personas,
  scores,
  userId,
}) => {
  // ==============================
  // Memoized values
  // ==============================

  // Extract commonly used properties from the `certificationSimulation`
  // simulation for easier access.
  const { description, status, title } = useMemo(
    () => certificationSimulation || {},
    [certificationSimulation],
  );

  /**
   * Maps the current `certificationSimulation` simulation into `RoleplayCardProps`
   * using the `mapSimulationToRoleplayCard` utility function.
   */
  const roleplayCardProps = useMemo(() => {
    return mapSimulationToRoleplayCard({
      simulation: certificationSimulation,
      personas,
      scores,
      onClick: onStartClick(certificationSimulation),
    });
  }, [certificationSimulation, personas, scores, onStartClick]);

  /**
   * Determines the action button props for the roleplay card based on the
   * simulation's status and history.
   */
  const roleplayCardAction = useMemo<ButtonProps>(() => {
    // Simplified constants for action button logic
    const { GET_STARTED, VIEW_CERTIFICATE } = BUTTON_LABELS;
    const isCompleted = status === 'completed';
    const isLocked = status === 'locked';

    // Return the action button props based on the simulation status and history
    return {
      label: certificationPassed || isCompleted ? VIEW_CERTIFICATE : GET_STARTED,
      onClick: onViewCertificateClick,
      type: 'button',
      variant: certificationPassed ? 'secondary' : 'primary',
      disabled: isLocked,
    };
  }, [certificationPassed, onViewCertificateClick, status]);

  // ==============================
  // Feature flag check
  // ==============================
  // If the final certification feature is not enabled, do not render anything.
  if (!FEATURE_FLAGS.ENABLE_FINAL_CERTIFICATION) {
    return null;
  }

  // ==============================
  // Render
  // ==============================
  return (
    <div className="relative mt-4 flex items-center justify-center">
      <RoleplayCard
        {...roleplayCardProps}
        actions={[roleplayCardAction]}
        className={getRedFailBorder(certificationSimulation, scores)}
        description={description}
        isLoading={isLoading}
        isNew={false}
        onShareWithManager={onShareWithManager}
        onViewScoreCard={onViewScoreCard}
        status={status}
        title={title}
        userId={userId}
      />
    </div>
  );
};
